import React from 'react'
import { capitalizeFirstLetter, formatDate } from '@btime/helpers'

import { Table, TableCol, TableRow } from '../../../../components/pdf/Table'
import { useServiceOrder } from '../_load/ServiceOrderLoader'
import Separator from '../../../../components/pdf/Separator'
import Image from '../../../../components/pdf/Image'
import View from '../../../../components/pdf/View'
import Text from '../../../../components/pdf/Text'
import Grid, { Col } from '../../../../components/pdf/Grid'
import {
  filterRepliesByParentReference,
  findReplyByQuestionId,
  getComplement,
  getRepliesFromBlockQuestion,
  getReplyValue,
  toCurrency
} from '../_helpers/pdfHelpers'
import Icon from '../../../../components/shapes/Icon'

const DisneyTemplate = () => {
  const parentServiceOrder = useServiceOrder()
  const replies = parentServiceOrder.replies
  const placeName = parentServiceOrder.place?.name
  const date = getReplyValue(findReplyByQuestionId(replies, 548))
  const month = formatDate('MMMM', date)
  const year = formatDate('YYYY', date)
  const userId = parentServiceOrder?.user?.id

  const paymentMethod = getReplyValue(findReplyByQuestionId(replies, 560))
  const generalObservation = getReplyValue(findReplyByQuestionId(replies, 595))
  const others = getReplyValue(findReplyByQuestionId(replies, 596))
  const generalImages = getReplyValue(findReplyByQuestionId(replies, 650)) || []
  const generalImagesList = generalImages.map(image => image.src)

  const userSignature = {
    5: '/pdfs/universal/assinatura-patricia.jpg',
    6: '/pdfs/universal/assinatura-claudia.jpg',
    7: '/pdfs/universal/assinatura-helena.jpg'
  }

  const airFareBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 549)
    const airFareReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const airFareResult = {}
    airFareReplies.forEach(airFareReply => {
      const blockIndex = airFareReply.blockIndex
      let airFareData = airFareResult[blockIndex]
      if (!airFareData) {
        const blockReplies = getRepliesFromBlockQuestion(airFareReplies, blockIndex)
        airFareData = {
          from: getReplyValue(findReplyByQuestionId(blockReplies, 550)),
          to: getReplyValue(findReplyByQuestionId(blockReplies, 551)),
          departureDate: formatDate('DD/MM/YYYY HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 552))),
          arrivalDate: formatDate('DD/MM/YYYY HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 553))),
          airCompany: getReplyValue(findReplyByQuestionId(blockReplies, 554)),
          baggage: getReplyValue(findReplyByQuestionId(blockReplies, 555))
        }
        airFareResult[blockIndex] = airFareData
      }
    })
    return Object.values(airFareResult)
  }

  const TaxBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 556)
    const TaxReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const taxResult = {}
    TaxReplies.forEach(taxReply => {
      const blockIndex = taxReply.blockIndex
      let taxData = taxResult[blockIndex]
      if (!taxData) {
        const blockReplies = getRepliesFromBlockQuestion(TaxReplies, blockIndex)
        taxData = {
          tax: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 646))),
          boardingTax: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 647))),
          total: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 648)))
        }
        taxResult[blockIndex] = taxData
      }
    })
    return Object.values(taxResult)
  }

  const ticketBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 568)
    const ticketReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const ticketResult = {}
    ticketReplies.forEach(ticketReply => {
      const blockIndex = ticketReply.blockIndex
      let ticketData = ticketResult[blockIndex]
      if (!ticketData) {
        const blockReplies = getRepliesFromBlockQuestion(ticketReplies, blockIndex)
        ticketData = {
          name: getReplyValue(findReplyByQuestionId(blockReplies, 569)),
          price: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 570)))
        }
        ticketResult[blockIndex] = ticketData
      }
    })
    return Object.values(ticketResult)
  }

  const accommodationBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 561)
    const accommodationReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const accommodationResult = {}
    accommodationReplies.forEach(accommodationReply => {
      const blockIndex = accommodationReply.blockIndex
      let accommodationData = accommodationResult[blockIndex]
      if (!accommodationData) {
        const blockReplies = getRepliesFromBlockQuestion(accommodationReplies, blockIndex)
        const images = getReplyValue(findReplyByQuestionId(blockReplies, 649)) || []
        const hotelImages = images.map(image => image.src)
        accommodationData = {
          hotelName: getReplyValue(findReplyByQuestionId(blockReplies, 562)),
          hotelAddress: getReplyValue(findReplyByQuestionId(blockReplies, 563)),
          hotelCategory: getReplyValue(findReplyByQuestionId(blockReplies, 564))?.name,
          hotelAlimentation: getReplyValue(findReplyByQuestionId(blockReplies, 565))
            .map(item => item.name)
            .join(', '),
          hotelPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 566))),
          hotelObservation: getReplyValue(findReplyByQuestionId(blockReplies, 567)),
          hotelImages
        }
        accommodationResult[blockIndex] = accommodationData
      }
    })
    return Object.values(accommodationResult)
  }

  const carRentBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 571)
    const carRentReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const carRentResult = {}
    carRentReplies.forEach(carRentReply => {
      const blockIndex = carRentReply.blockIndex
      let carRentData = carRentResult[blockIndex]
      if (!carRentData) {
        const blockReplies = getRepliesFromBlockQuestion(carRentReplies, blockIndex)
        carRentData = {
          carRentStartDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 572))),
          carRentEndDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 573))),
          carRentModel: getReplyValue(findReplyByQuestionId(blockReplies, 574)),
          carRentPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 575)))
        }
        carRentResult[blockIndex] = carRentData
      }
    })
    return Object.values(carRentResult)
  }

  const transferBlock = () => {
    const blockReply = findReplyByQuestionId(replies, 581)
    const transferReplies = filterRepliesByParentReference(replies, blockReply?.question?.reference)

    const transferResult = {}
    transferReplies.forEach(trainReply => {
      const blockIndex = trainReply.blockIndex
      let transferData = transferResult[blockIndex]
      if (!transferData) {
        const blockReplies = getRepliesFromBlockQuestion(transferReplies, blockIndex)
        transferData = {
          transferStartPlace: getReplyValue(findReplyByQuestionId(blockReplies, 582)),
          transferEndPlace: getReplyValue(findReplyByQuestionId(blockReplies, 583)),
          transferStartDate: formatDate('DD/MM', getReplyValue(findReplyByQuestionId(blockReplies, 584))),
          transferStartTime: formatDate('HH:MM', getReplyValue(findReplyByQuestionId(blockReplies, 584))),
          transferPrice: toCurrency(getReplyValue(findReplyByQuestionId(blockReplies, 585)))
        }
        transferResult[blockIndex] = transferData
      }
    })
    return Object.values(transferResult)
  }

  const airFareReplies = airFareBlock()
  const taxReplies = TaxBlock()
  const ticketReplies = ticketBlock()
  const accommodationReplies = accommodationBlock()
  const carRentReplies = carRentBlock()
  const transferReplies = transferBlock()

  return (
    <View pageBreakAfter>
      <Grid justify="center" align="center">
        <Image src="/pdfs/universal/disney/disney-header.jpg" width="100%" />
      </Grid>
      <Separator weight={10} />

      <Grid justify="center">
        <Text size={32} bold>
          {placeName} - {capitalizeFirstLetter(month)} {year}
        </Text>
      </Grid>

      <Separator weight={10} />

      {!!airFareReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/disney/passagens-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625" style={{ borderTop: '1px solid #fff' }}>
            <TableRow color="#bb3625">
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#bb3625">
                <Text color="#ffffff" style={{ fontWeight: 700 }}>
                  De
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text color="#ffffff" style={{ fontWeight: 700 }}>
                  Para
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Data e Hora saída
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Data e Hora chegada
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Bagagem
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Cia Aérea
                </Text>
              </TableCol>
            </TableRow>
            {airFareReplies.map(reply => {
              return (
                <>
                  <TableRow color="#bb3625">
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.from}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.to}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.departureDate}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.arrivalDate}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.baggage ? 'Sim' : 'Não'}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.airCompany}</Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={5} />
        </>
      )}

      {!!taxReplies.length && (
        <>
          <Table width="100%" color="#bb3625">
            <TableRow color="#bb3625">
              <TableCol width="33%" bgColor="#bb3625" style={{ padding: '10px' }} color="#bb3625">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor da Tarifa
                </Text>
              </TableCol>
              <TableCol width="33%" bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor da Taxa de Embarque
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor Total
                </Text>
              </TableCol>
            </TableRow>
            {taxReplies.map(reply => {
              return (
                <>
                  <TableRow color="#bb3625">
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.tax}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.boardingTax}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.total}</Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={5} />
          <Table width="100%" color="#bb3625">
            <TableRow color="#bb3625">
              <TableCol width="45%" bgColor="#bb3625" style={{ padding: '10px' }}>
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Forma de pagamento:
                </Text>
              </TableCol>
              <TableCol style={{ padding: '10px' }}>
                <Text>{paymentMethod}</Text>
              </TableCol>
            </TableRow>
          </Table>

          <Separator weight={10} />
        </>
      )}

      {!!accommodationReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/disney/hospedagem-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625" style={{ borderTop: '1px solid #fff' }}>
            <TableRow color="#bb3625">
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#bb3625">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Nome do Hotel
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Categoria
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Alimentação
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Valor
                </Text>
              </TableCol>
              <TableCol bgColor="#bb3625" style={{ padding: '10px' }} color="#fff">
                <Text bold color="#ffffff" style={{ fontWeight: 700 }}>
                  Observação
                </Text>
              </TableCol>
            </TableRow>
            {accommodationReplies.map(reply => {
              return (
                <>
                  <TableRow color="#bb3625">
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>🏨 {reply.hotelName}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.hotelCategory}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.hotelAlimentation}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.hotelPrice}</Text>
                    </TableCol>
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>{reply.hotelObservation}</Text>
                    </TableCol>
                  </TableRow>
                  <TableRow color="#bb3625">
                    <TableCol bgColor="#bb3625" style={{ padding: '10px', borderTop: '1px solid #fff' }}>
                      <Text color="#ffffff" style={{ fontWeight: 700 }}>
                        Endereço do Hotel
                      </Text>
                    </TableCol>
                    <TableCol colSpan={4} style={{ padding: '10px' }} color="#bb3625">
                      <a
                        href={`https://maps.google.com/maps?q=${encodeURIComponent(reply.hotelAddress.search)}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          textDecoration: 'none',
                          width: 'fit-content'
                        }}
                      >
                        <Text>{`📍 ${reply.hotelAddress.search} ${getComplement(
                          reply.hotelAddress?.complement
                        )}`}</Text>
                        <Icon name="BsLink45Deg" size="1rem" color="#000000" />
                      </a>
                    </TableCol>
                  </TableRow>
                  {!!reply.hotelImages.length && (
                    <>
                      <TableRow color="#bb3625">
                        <TableCol
                          colSpan={5}
                          bgColor="#bb3625"
                          style={{ padding: '10px', borderTop: '1px solid #fff' }}
                        >
                          <Text color="#ffffff" style={{ fontWeight: 700 }} align="center">
                            Fotos do Hotel
                          </Text>
                        </TableCol>
                      </TableRow>
                      <TableRow color="#bb3625">
                        <TableCol colSpan={5} style={{ padding: '10px' }} color="#bb3625">
                          <Grid direction="row" wrap>
                            {reply.hotelImages?.map((image, index) => {
                              return (
                                <Col key={index} width="50%" gutter={5}>
                                  <Image src={image} width="100%" />
                                </Col>
                              )
                            })}
                          </Grid>
                        </TableCol>
                      </TableRow>
                    </>
                  )}
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!ticketReplies.length && (
        <>
          <Grid>
            <Image src="/pdfs/universal/disney/ingressos-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625">
            {ticketReplies.map(reply => {
              return (
                <>
                  <TableRow color="#bb3625">
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>
                        🎫 {reply.name} - {reply.price} por pessoa
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!carRentReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/disney/aluguel-carro-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625">
            {carRentReplies.map(reply => {
              return (
                <>
                  <TableRow color="#bb3625">
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>
                        🚘 Aluguel de carro {reply.carRentModel} de {reply.carRentStartDate} a {reply.carRentEndDate} -{' '}
                        {reply.carRentPrice}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!transferReplies.length && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/disney/traslado-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625">
            {transferReplies.map(reply => {
              return (
                <>
                  <TableRow color="#bb3625">
                    <TableCol style={{ padding: '10px' }} color="#bb3625">
                      <Text>
                        🚌 Transfer {reply.transferStartPlace} a {reply.transferEndPlace} dia {reply.transferStartDate}{' '}
                        - {reply.transferPrice} - Saída às {reply.transferStartTime}
                      </Text>
                    </TableCol>
                  </TableRow>
                </>
              )
            })}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!generalObservation && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/disney/observacoes-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625">
            <TableRow color="#bb3625">
              <TableCol style={{ padding: '10px' }} color="#bb3625">
                <Text>📝 {generalObservation}</Text>
              </TableCol>
            </TableRow>
            {!!generalImagesList.length && (
              <>
                <TableRow color="#bb3625">
                  <TableCol style={{ padding: '10px' }} bgColor="#bb3625">
                    <Text color="#ffffff" style={{ fontWeight: 700 }} align="center">
                      Fotos gerais
                    </Text>
                  </TableCol>
                </TableRow>
                <TableRow color="#bb3625">
                  <TableCol colSpan={5} style={{ padding: '10px' }} color="#bb3625">
                    <Grid direction="row" wrap>
                      {generalImagesList.map((image, index) => {
                        return (
                          <Col key={index} width="50%" gutter={5}>
                            <Image src={image} width="100%" />
                          </Col>
                        )
                      })}
                    </Grid>
                  </TableCol>
                </TableRow>
              </>
            )}
          </Table>
          <Separator weight={10} />
        </>
      )}

      {!!others && (
        <>
          <Grid justify="center" align="center">
            <Image src="/pdfs/universal/disney/outros-banner.jpg" width="100%" />
          </Grid>
          <Table width="100%" color="#bb3625">
            <TableRow color="#bb3625">
              <TableCol style={{ padding: '10px' }} color="#bb3625">
                <Text>📌 {others}</Text>
              </TableCol>
            </TableRow>
          </Table>
          <Separator weight={10} />
        </>
      )}

      <Grid justify="center" align="center">
        <Image src="/pdfs/universal/disney/informacoes-banner.jpg" width="100%" />
      </Grid>

      <Separator weight={10} />

      <Grid justify="center" align="center">
        <Image src={userSignature[userId]} width={400} />
      </Grid>
    </View>
  )
}

export default DisneyTemplate
